"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useSearchParams } from "next/navigation";
import { submitContactAction } from "../app/actions/contact";
import { useSiteData } from "../lib/site-context";
import { useLangContext } from "../lib/lang-context";

const STATUS_IDLE = "idle";
const STATUS_SUCCESS = "success";
const STATUS_ERROR = "error";

export default function ContactForm() {
  const { t, lang } = useLangContext();
  const { parts, services, machines } = useSiteData();
  // Pré-popula `interest` (qual produto/serviço/máquina o usuário veio
  // querendo) e `searchQuery` (o termo que ele buscou e não achou) a partir
  // da query string da URL — vem do CTA "Solicitar Orçamento" das outras
  // páginas e do "Fale com a engenharia" do estado vazio do catálogo.
  const searchParams = useSearchParams();
  const [interest, setInterest] = useState(
    () => searchParams?.get("interest") ?? ""
  );
  const [searchQuery] = useState(
    () => searchParams?.get("searchQuery") ?? ""
  );
  const [status, setStatus] = useState(STATUS_IDLE);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isPending, startTransition] = useTransition();
  const successRef = useRef(null);

  // O form tem ~1000px e o aviso de sucesso ~140px: como o scroll é preservado
  // na troca, quem enviou do fim do form fica olhando pro rodapé e lê isso como
  // envio falhado.
  useEffect(() => {
    if (status !== STATUS_SUCCESS) return;
    const reduz = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    successRef.current?.scrollIntoView({
      behavior: reduz ? "auto" : "smooth",
      block: "center",
    });
  }, [status]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    startTransition(async () => {
      const result = await submitContactAction(formData);
      if (result?.ok) {
        setStatus(STATUS_SUCCESS);
        setErrorMsg(null);
        form.reset();
      } else {
        setStatus(STATUS_ERROR);
        setErrorMsg(result?.error ?? "");
      }
    });
  };

  if (status === STATUS_SUCCESS) {
    return (
      <div
        ref={successRef}
        role="status"
        style={{ border: "1px solid var(--ink)", padding: 24 }}
      >
        <div className="label-num" style={{ color: "var(--brand)" }}>
          {t("contact_success_label")}
        </div>
        <h4 className="h-display" style={{ fontSize: 24, margin: "8px 0" }}>
          {t("contact_success_title")}
        </h4>
        <p style={{ color: "var(--ink-3)", margin: 0 }}>
          {t("contact_success_message")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="cf-name">{t("f_name")}</label>
          <input
            id="cf-name"
            name="name"
            required
            autoComplete="name"
            disabled={isPending}
          />
        </div>
        <div className="field">
          <label htmlFor="cf-company">{t("f_company")}</label>
          <input
            id="cf-company"
            name="company"
            required
            autoComplete="organization"
            disabled={isPending}
          />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="cf-email">{t("f_email")}</label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            inputMode="email"
            autoComplete="email"
            disabled={isPending}
          />
        </div>
        <div className="field">
          <label htmlFor="cf-phone">{t("f_phone")}</label>
          <input
            id="cf-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            disabled={isPending}
          />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="cf-press">{t("f_press")}</label>
          <input
            id="cf-press"
            name="press"
            placeholder={t("f_press_placeholder")}
            disabled={isPending}
          />
        </div>
        <div className="field">
          <label htmlFor="cf-interest">{t("f_part")}</label>
          <select
            id="cf-interest"
            name="interest"
            value={interest}
            onChange={(event) => setInterest(event.target.value)}
            disabled={isPending}
          >
            <option value="">—</option>
            {parts.map((p) => (
              <option key={`part:${p.id ?? p.ref}`} value={p.id ? `part:${p.id}` : ""}>
                {p.name[lang]}
              </option>
            ))}
            {services.map((s) => (
              <option key={`svc:${s.code}`} value={s.id ? `svc:${s.id}` : ""}>
                {s.name[lang]}
              </option>
            ))}
            {machines.map((m) => (
              <option
                key={`machine:${m.id ?? m.ref}`}
                value={m.id ? `machine:${m.id}` : ""}
              >
                {m.name[lang]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="cf-message">{t("f_message")}</label>
        <textarea id="cf-message" name="message" rows="4" disabled={isPending} />
      </div>

      {/* Consent obrigatório (LGPD). */}
      <input type="hidden" name="consent" value="on" />

      {/* Contexto da busca que veio sem resultados (CTA "Fale com a engenharia"). */}
      {searchQuery && (
        <input type="hidden" name="searchQuery" value={searchQuery} />
      )}

      {status === STATUS_ERROR && (
        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: "10px 14px",
            border: "1px solid var(--accent, #B8341B)",
            color: "var(--accent, #B8341B)",
            fontSize: 13,
            fontFamily: "var(--mono)",
          }}
        >
          {t("contact_error_message")}
          {errorMsg ? ` (${errorMsg})` : ""}
        </div>
      )}

      <button
        type="submit"
        className="cta-btn"
        style={{ marginTop: 16, opacity: isPending ? 0.6 : 1 }}
        disabled={isPending}
      >
        {isPending ? t("submitting") : t("submit")} <span className="arr" aria-hidden="true">→</span>
      </button>
    </form>
  );
}
